import type { ParentLinkSchema } from '@repo/schemas/parent/parentLink';
import type { GradePublic } from '@repo/types/grades';
import type { PaginatedResponse } from '@repo/types/pagination';
import type { DatetimeCursorPagination, PaginationWithSort } from '@repo/types/pagination';
import type { ParentAttendanceSummary, ParentCalendarMeeting, ParentChildAssignment, ParentChildAttendance, ParentChildGroup, ParentChildMeeting, ParentChildQuizAttempt } from '@repo/types/parent';
import type { ParentGroupFilter } from '@repo/types/parent';
import type { UserPrivate, UserPublic } from '@repo/types/users';
import type { AxiosResponse } from 'axios';

import { axiosInstance } from '#api-client';

export class ParentService {
  static async getParents(): Promise<AxiosResponse<Array<UserPrivate>>> {
    return await axiosInstance.get('/api/parent');
  }

  static async getStudentParents(studentId: number): Promise<AxiosResponse<Array<UserPublic>>> {
    return await axiosInstance.get(`/api/parent/student/${studentId}`);
  }

  static async getParentChildren(parentId: number): Promise<AxiosResponse<Array<UserPublic>>> {
    return await axiosInstance.get(`/api/parent/${parentId}/children`);
  }

  static async linkParent(data: ParentLinkSchema): Promise<AxiosResponse<{ parentId: number; studentId: number }>> {
    return await axiosInstance.post('/api/parent/link', data);
  }

  static async unlinkParent(data: ParentLinkSchema): Promise<AxiosResponse<{ title: string; status: number; detail: string }>> {
    return await axiosInstance.delete('/api/parent/link', { data });
  }

  /**
   * Children linked to the *authenticated* parent.
   *
   * Distinct from `getParentChildren`, which reads any parent's children and is
   * restricted to teachers and admins.
   */
  static async getMyChildren(): Promise<AxiosResponse<Array<UserPublic>>> {
    return await axiosInstance.get('/api/parent/children');
  }

  /**
   * Every linked child's meetings, merged into one calendar.
   *
   * Distinct from calling getChildSchedule per child: a meeting several
   * siblings attend comes back once, tagged with all of them.
   */
  static async getParentCalendar(params?: GetChildScheduleParams): Promise<AxiosResponse<Array<ParentCalendarMeeting>>> {
    return await axiosInstance.get('/api/parent/calendar', { params });
  }

  static async getChildSchedule(childId: number, params?: GetChildScheduleParams): Promise<AxiosResponse<Array<ParentChildMeeting>>> {
    return await axiosInstance.get(`/api/parent/children/${childId}/schedule`, { params });
  }

  static async getChildGrades(childId: number, params?: GetChildListParams): Promise<AxiosResponse<PaginatedResponse<GradePublic>>> {
    return await axiosInstance.get(`/api/parent/children/${childId}/grades`, {
      params,
    });
  }

  static async getChildAttendance(childId: number, params?: GetChildListParams): Promise<AxiosResponse<PaginatedResponse<ParentChildAttendance>>> {
    return await axiosInstance.get(`/api/parent/children/${childId}/attendance`, { params });
  }

  /**
   * Global (all-time, all groups) attendance summary of a linked child.
   */
  static async getChildAttendanceSummary(childId: number): Promise<AxiosResponse<ParentAttendanceSummary>> {
    return await axiosInstance.get(`/api/parent/children/${childId}/attendance/summary`);
  }

  static async getChildGroups(childId: number): Promise<AxiosResponse<Array<ParentChildGroup>>> {
    return await axiosInstance.get(`/api/parent/children/${childId}/groups`);
  }

  static async getChildQuizzes(childId: number, params?: GetChildListParams): Promise<AxiosResponse<PaginatedResponse<ParentChildQuizAttempt>>> {
    return await axiosInstance.get(`/api/parent/children/${childId}/quizzes`, {
      params,
    });
  }

  static async getChildAssignments(childId: number, params?: GetChildListParams): Promise<AxiosResponse<PaginatedResponse<ParentChildAssignment>>> {
    return await axiosInstance.get(`/api/parent/children/${childId}/assignments`, { params });
  }

  /**
   * One assignment set to a linked child, with that child's submissions.
   *
   * Responds 404 when the assignment is not set to that child.
   */
  static async getChildAssignment(childId: number, assignmentId: number): Promise<AxiosResponse<ParentChildAssignment>> {
    return await axiosInstance.get(`/api/parent/children/${childId}/assignments/${assignmentId}`);
  }

  /**
   * One completed quiz attempt of a linked child, with its quiz and grade.
   *
   * Responds 404 when the attempt is not that child's, or is unfinished.
   */
  static async getChildQuizAttempt(childId: number, attemptId: string): Promise<AxiosResponse<ParentChildQuizAttempt>> {
    return await axiosInstance.get(`/api/parent/children/${childId}/quizzes/${attemptId}`);
  }

  static async getChildTeachers(childId: number): Promise<AxiosResponse<Array<UserPublic>>> {
    return await axiosInstance.get(`/api/parent/children/${childId}/teachers`);
  }
}

/**
 * Query for `GET /api/parent/children/:childId/schedule`.
 *
 * Same window-and-page shape as `GetMeetingsParams`, minus the `id` filter a
 * parent has no use for. Omitting `pageSize` returns the whole window; the
 * backend defaults `sortOrder` to `DESC`.
 */
export interface GetChildScheduleParams extends Partial<DatetimeCursorPagination>, Partial<PaginationWithSort> {}

/**
 * Query for the paginated child list endpoints
 * (`grades` / `attendance` / `assignments` / `quizzes`).
 *
 * Backend defaults: `page` 0, `pageSize` 20 (clamped to 1–100), `sortOrder`
 * `DESC`. `name` is a case/accent-insensitive substring search; `groupId`
 * narrows to one group, or to ungrouped rows with `'none'`.
 */
export interface GetChildListParams {
  page?: number;
  pageSize?: number;
  name?: string;
  sortOrder?: 'ASC' | 'DESC';
  groupId?: ParentGroupFilter;
}
